import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import {
  WebsiteBuilderDraftState,
  WebsitePageDocument
} from '../models/website-builder.models';
import { PageRevisionSummary, WebsiteDataCollection, WebsitePageSummary, WebsiteSummary } from '../models/website-management.models';

export interface WebsiteBuilderApiError {
  code: number;
  message: string;
  details?: string[];
}

interface PageDraftResponse {
  success: boolean;
  website: WebsiteSummary;
  page: WebsitePageSummary;
  pages: WebsitePageSummary[];
  dataCollections: WebsiteDataCollection[];
  draftRevision: PageRevisionSummary | null;
  document: WebsitePageDocument;
}

@Injectable({
  providedIn: 'root'
})
export class WebsiteBuilderApiService {
  private readonly API_URL = environment.apiUrl;

  constructor(private readonly http: HttpClient) {}

  getDraft(websitePublicId: string, pagePublicId: string): Observable<WebsiteBuilderDraftState> {
    return this.http
      .get<PageDraftResponse>(`${this.API_URL}/websites/${websitePublicId}/pages/${pagePublicId}/draft`)
      .pipe(
        map((response) => ({
          website: response.website,
          page: response.page,
          pages: response.pages,
          dataCollections: response.dataCollections ?? [],
          draftRevision: response.draftRevision,
          document: response.document
        })),
        catchError(this.handleError)
      );
  }

  updateDraft(websitePublicId: string, pagePublicId: string, document: WebsitePageDocument): Observable<WebsiteBuilderDraftState> {
    return this.http
      .put<PageDraftResponse>(`${this.API_URL}/websites/${websitePublicId}/pages/${pagePublicId}/draft`, { document })
      .pipe(
        map((response) => ({
          website: response.website,
          page: response.page,
          pages: response.pages,
          dataCollections: response.dataCollections ?? [],
          draftRevision: response.draftRevision,
          document: response.document
        })),
        catchError(this.handleError)
      );
  }

  updateDraftKeepalive(websitePublicId: string, pagePublicId: string, document: WebsitePageDocument): void {
    if (typeof fetch === 'undefined') {
      return;
    }

    void fetch(`${this.API_URL}/websites/${websitePublicId}/pages/${pagePublicId}/draft`, {
      method: 'PUT',
      credentials: 'include',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ document })
    }).catch(() => undefined);
  }

  private handleError = (error: HttpErrorResponse): Observable<never> => {
    let message = 'An unexpected error occurred';

    if (error.error?.error?.message) {
      message = error.error.error.message as string;
    } else if (error.error?.message) {
      message = error.error.message as string;
    } else if (error.message) {
      message = error.message;
    }

    return throwError(() => ({
      code: error.status,
      message,
      details: error.error?.error?.details as string[] | undefined
    } as WebsiteBuilderApiError));
  };
}
